import { useEffect, useRef } from "react";
import { gsap, prefersReducedMotion, revealWords } from "../lib/motion";
import { DOWNLOAD_URL } from "../data/site";
import Button from "./ui/Button";
import ctaPhones from "../assets/images/cta-phones.webp";
import waveCta from "../assets/decor/wave-cta.webp";
import "./CallToAction.css";

export default function CallToAction() {
  const rootRef = useRef(null);
  const phonesRef = useRef(null);
  const bandRef = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const ctx = gsap.context(() => {
      revealWords(root.querySelector(".cta__title"), { trigger: root });

      if (prefersReducedMotion()) {
        gsap.set([bandRef.current, phonesRef.current, ".cta__actions"], {
          autoAlpha: 1,
          clipPath: "none",
          y: 0,
          scale: 1,
        });
        return;
      }

      // Reveal the sand plate only — clipping the whole band would crop the
      // mockup that deliberately overhangs its top edge.
      gsap.fromTo(
        bandRef.current,
        { clipPath: "inset(0% 0% 100% 0%)" },
        {
          clipPath: "inset(0% 0% 0% 0%)",
          duration: 1.25,
          ease: "expo.out",
          scrollTrigger: { trigger: root, start: "top 82%", once: true },
          onComplete: () => gsap.set(bandRef.current, { clearProps: "clipPath" }),
        }
      );

      gsap.fromTo(
        phonesRef.current,
        { autoAlpha: 0, y: 80, rotate: -5, scale: 0.94 },
        {
          autoAlpha: 1,
          y: 0,
          rotate: 0,
          scale: 1,
          duration: 1.6,
          ease: "expo.out",
          delay: 0.18,
          scrollTrigger: { trigger: root, start: "top 82%", once: true },
        }
      );

      gsap.fromTo(
        ".cta__actions",
        { autoAlpha: 0, y: 26 },
        {
          autoAlpha: 1,
          y: 0,
          duration: 0.9,
          ease: "power3.out",
          delay: 0.45,
          scrollTrigger: { trigger: root, start: "top 82%", once: true },
        }
      );

      gsap.to(phonesRef.current, {
        yPercent: -8,
        ease: "none",
        scrollTrigger: { trigger: root, start: "top bottom", end: "bottom top", scrub: 0.6 },
      });
    }, root);

    return () => ctx.revert();
  }, []);

  return (
    <section className="cta section" ref={rootRef}>
      <div className="container">
        <div className="cta__band">
          <div className="cta__plate" ref={bandRef} aria-hidden="true">
            <div className="cta__wave" style={{ backgroundImage: `url(${waveCta})` }} />
          </div>
          <img
            ref={phonesRef}
            className="cta__phones"
            src={ctaPhones}
            alt="Two phones showing the Private Stays app splash and booking screens."
            width="800"
            height="824"
            loading="lazy"
            decoding="async"
          />
          <div className="cta__copy">
            <h2 className="cta__title">Get The App, Download NOW!</h2>
            <div className="cta__actions">
              <Button href={DOWNLOAD_URL} variant="dark" className="cta__btn">
                Download now
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
